import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { ChevronLeft, ChevronRight, Calendar, Building2, Users, MapPin, Clock, IndianRupee } from 'lucide-react';
import { useDashboardStore } from '../stores/dashboardStore';
import CommonActionBar from '../components/CommonActionBar';

interface CorporateOuting {
  id: number;
  companyName: string;
  companyLogo: string;
  eventName: string;
  eventDate: string;
  startTime: string;
  endTime: string;
  location: string;
  attendees: number;
  totalAmount: number;
  status: 'confirmed' | 'pending' | 'cancelled';
  contactPerson: string;
  contactPhone: string;
  specialRequirements?: string;
  bookingSource: string;
}

const CorporateOutings: React.FC = () => {
  const [outings, setOutings] = useState<CorporateOuting[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<'week' | 'month'>('week');

  // Mock data for corporate outings - Mix of Indian and International companies
  const mockOutings: CorporateOuting[] = [
    {
      id: 1,
      companyName: 'TCS',
      companyLogo: '/images/companies/tcs.png',
      eventName: 'Annual Leadership Summit',
      eventDate: '2024-01-15',
      startTime: '09:00',
      endTime: '18:00',
      location: 'Grand Ballroom',
      attendees: 200,
      totalAmount: 450000,
      status: 'confirmed',
      contactPerson: 'Priya Sharma',
      contactPhone: '+91-98765-43210',
      specialRequirements: 'AV equipment, catering for 200 people, cultural performances',
      bookingSource: 'Direct Booking'
    },
    {
      id: 2,
      companyName: 'Amazon India',
      companyLogo: '/images/companies/amazon.png',
      eventName: 'AWS Innovation Summit',
      eventDate: '2024-01-18',
      startTime: '19:00',
      endTime: '23:00',
      location: 'Executive Lounge',
      attendees: 120,
      totalAmount: 250000,
      status: 'confirmed',
      contactPerson: 'Rajesh Kumar',
      contactPhone: '+91-98765-43211',
      bookingSource: 'Corporate Portal'
    },
    {
      id: 3,
      companyName: 'JP Morgan Chase',
      companyLogo: '/images/companies/jpmorgan.png',
      eventName: 'Investment Banking Conference',
      eventDate: '2024-01-22',
      startTime: '18:30',
      endTime: '22:30',
      location: 'Sky Lounge',
      attendees: 150,
      totalAmount: 600000,
      status: 'pending',
      contactPerson: 'Anita Reddy',
      contactPhone: '+91-98765-43212',
      specialRequirements: 'Live streaming setup, premium bar service, international cuisine',
      bookingSource: 'Event Management'
    },
    {
      id: 4,
      companyName: 'Infosys',
      companyLogo: '/images/companies/infosys.png',
      eventName: 'Digital Innovation Workshop',
      eventDate: '2024-01-25',
      startTime: '10:00',
      endTime: '16:00',
      location: 'Conference Center',
      attendees: 100,
      totalAmount: 350000,
      status: 'confirmed',
      contactPerson: 'Vikram Singh',
      contactPhone: '+91-98765-43213',
      specialRequirements: 'Workshop materials, lunch for 100 people, tech demonstrations',
      bookingSource: 'Direct Booking'
    },
    {
      id: 5,
      companyName: 'Microsoft India',
      companyLogo: '/images/companies/microsoft.png',
      eventName: 'Azure Cloud Summit',
      eventDate: '2024-01-28',
      startTime: '14:00',
      endTime: '17:00',
      location: 'Boardroom',
      attendees: 30,
      totalAmount: 120000,
      status: 'confirmed',
      contactPerson: 'Deepak Patel',
      contactPhone: '+91-98765-43214',
      bookingSource: 'Corporate Portal'
    },
    {
      id: 6,
      companyName: 'Reliance Industries',
      companyLogo: '/images/companies/reliance.png',
      eventName: 'Jio Digital Transformation',
      eventDate: '2024-01-30',
      startTime: '11:00',
      endTime: '19:00',
      location: 'Garden Pavilion',
      attendees: 80,
      totalAmount: 280000,
      status: 'pending',
      contactPerson: 'Sunita Gupta',
      contactPhone: '+91-98765-43215',
      specialRequirements: 'Outdoor activities setup, team building materials, traditional games',
      bookingSource: 'Event Management'
    },
    {
      id: 7,
      companyName: 'Google India',
      companyLogo: '/images/companies/google.png',
      eventName: 'AI & ML Conference',
      eventDate: '2024-02-02',
      startTime: '09:30',
      endTime: '17:30',
      location: 'Convention Hall',
      attendees: 300,
      totalAmount: 750000,
      status: 'confirmed',
      contactPerson: 'Arjun Mehta',
      contactPhone: '+91-98765-43216',
      specialRequirements: 'Multiple breakout rooms, live streaming, international cuisine',
      bookingSource: 'Direct Booking'
    },
    {
      id: 8,
      companyName: 'Wipro',
      companyLogo: '/images/companies/wipro.png',
      eventName: 'Client Appreciation Gala',
      eventDate: '2024-02-05',
      startTime: '10:00',
      endTime: '16:00',
      location: 'Main Auditorium',
      attendees: 180,
      totalAmount: 420000,
      status: 'confirmed',
      contactPerson: 'Kavya Nair',
      contactPhone: '+91-98765-43217',
      specialRequirements: 'Presentation equipment, lunch for 180 people',
      bookingSource: 'Corporate Portal'
    }
  ];

  useEffect(() => {
    // Simulate API call
    const loadOutings = async () => {
      setIsLoading(true);
      try {
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 1000));
        setOutings(mockOutings);
        setError(null);
      } catch (err) {
        setError('Failed to load corporate outings');
      } finally {
        setIsLoading(false);
      }
    };

    loadOutings();
  }, []);

  const handleRefresh = () => {
    setIsLoading(true);
    setTimeout(() => {
      setOutings(mockOutings);
      setIsLoading(false);
    }, 1000);
  };

  const navigateDate = (direction: number) => {
    const newDate = new Date(selectedDate);
    if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() + (direction * 7));
    } else {
      newDate.setMonth(newDate.getMonth() + direction);
    }
    setSelectedDate(newDate);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getCompanyLogo = (companyName: string) => {
    // Return a placeholder or default logo
    return (
      <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center text-white font-bold text-lg">
        {companyName.charAt(0)}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading corporate outings...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-red-600 text-2xl">⚠️</span>
          </div>
          <h3 className="text-lg font-bold text-red-600 mb-2">Error</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={handleRefresh}
            className="btn-primary"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const formatDate = (date: Date) => {
    if (viewMode === 'week') {
      const startOfWeek = new Date(date);
      startOfWeek.setDate(date.getDate() - date.getDay());
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      return `${format(startOfWeek, 'MMM dd')} - ${format(endOfWeek, 'MMM dd')}`;
    } else {
      return format(date, 'MMMM yyyy');
    }
  };

  return (
    <div className="space-y-6">
      {/* Common Action Bar */}
      <CommonActionBar
        showDateTime={false}
        centerAlign={true}
        centerContent={
          <div className="flex items-center space-x-4">
            {/* Previous Button */}
            <button
              onClick={() => navigateDate(-1)}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-4 h-4 text-gray-600" />
              <span className="text-sm font-medium text-gray-700">Previous</span>
            </button>

            {/* Date Display */}
            <div className="flex items-center space-x-2">
              <Calendar className="w-4 h-4 text-gray-600" />
              <span className="text-lg font-medium text-gray-800">{formatDate(selectedDate)}</span>
            </div>

            {/* Next Button */}
            <button
              onClick={() => navigateDate(1)}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <span className="text-sm font-medium text-gray-700">Next</span>
              <ChevronRight className="w-4 h-4 text-gray-600" />
            </button>

            {/* View Mode Toggle */}
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('week')}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'week' 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Week
              </button>
              <button
                onClick={() => setViewMode('month')}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'month' 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Month
              </button>
            </div>
          </div>
        }
        onRefresh={handleRefresh}
      />

      {/* Corporate Outings Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {outings.map((outing) => (
          <div
            key={outing.id}
            className="glass-card p-6 rounded-xl hover:shadow-lg transition-all duration-200 border border-gray-200"
          >
            {/* Company Header */}
            <div className="flex items-center space-x-4 mb-4">
              {getCompanyLogo(outing.companyName)}
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900">{outing.companyName}</h3>
                <p className="text-sm text-gray-600">{outing.eventName}</p>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(outing.status)}`}>
                {outing.status}
              </span>
            </div>

            {/* Event Details */}
            <div className="space-y-3">
              <div className="flex items-center space-x-3 text-sm text-gray-600">
                <Calendar className="w-4 h-4 text-primary-500" />
                <span>{format(new Date(outing.eventDate), 'MMM dd, yyyy')}</span>
              </div>
              
              <div className="flex items-center space-x-3 text-sm text-gray-600">
                <Clock className="w-4 h-4 text-primary-500" />
                <span>{outing.startTime} - {outing.endTime}</span>
              </div>
              
              <div className="flex items-center space-x-3 text-sm text-gray-600">
                <MapPin className="w-4 h-4 text-primary-500" />
                <span>{outing.location}</span>
              </div>
              
              <div className="flex items-center space-x-3 text-sm text-gray-600">
                <Users className="w-4 h-4 text-primary-500" />
                <span>{outing.attendees} attendees</span>
              </div>
              
              <div className="flex items-center space-x-3 text-sm text-gray-600">
                <IndianRupee className="w-4 h-4 text-primary-500" />
                <span className="font-medium text-gray-900">₹{outing.totalAmount.toLocaleString()}</span>
              </div>
            </div>

            {/* Contact Information */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="text-sm">
                <p className="text-gray-600">
                  <span className="font-medium">Contact:</span> {outing.contactPerson}
                </p>
                <p className="text-gray-600">
                  <span className="font-medium">Phone:</span> {outing.contactPhone}
                </p>
                <p className="text-gray-600">
                  <span className="font-medium">Source:</span> {outing.bookingSource}
                </p>
              </div>
            </div>

            {/* Special Requirements */}
            {outing.specialRequirements && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Special Requirements:</span>
                </p>
                <p className="text-sm text-gray-700 mt-1">{outing.specialRequirements}</p>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Empty State */}
      {outings.length === 0 && (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Building2 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Corporate Outings</h3>
            <p className="text-gray-600">No corporate outings found for the selected period.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CorporateOutings;
